<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  calendarViewMode: {
    type: String,
    default: 'month',
  },
  todayTrigger: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['openConversation', 'deleteMessage']);
const { t } = useI18n();

const currentMonth = ref(new Date());
const selectedDate = ref(new Date());
const showDayEventsModal = ref(false);

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const parseScheduledDate = rawDate => {
  if (rawDate === null || rawDate === undefined || rawDate === '') return null;

  if (typeof rawDate === 'number') {
    const date = new Date(rawDate > 1_000_000_000_000 ? rawDate : rawDate * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof rawDate === 'string') {
    if (/^\d+$/.test(rawDate)) {
      const numericValue = Number(rawDate);
      const date = new Date(
        numericValue > 1_000_000_000_000 ? numericValue : numericValue * 1000
      );
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      const [year, month, day] = rawDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsedDate = new Date(rawDate);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(rawDate);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isSameDay = (dateA, dateB) => {
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

const isToday = date => isSameDay(date, new Date());
const dayHours = Array.from({ length: 24 }, (_, index) => index);

const getEventsForDate = date => {
  return props.messages.filter(msg => {
    if (!msg?.scheduled_at) return false;
    const parsedDate = parseScheduledDate(msg.scheduled_at);
    if (!parsedDate) return false;
    return isSameDay(parsedDate, date);
  });
};

const getEventsForHour = (events, hour) => {
  return events
    .filter(event => {
      if (!event?.scheduled_at) return false;
      const parsedDate = parseScheduledDate(event.scheduled_at);
      if (!parsedDate) return false;
      return parsedDate.getHours() === hour;
    })
    .sort((a, b) => (a.scheduled_at || 0) - (b.scheduled_at || 0));
};

const formatHourLabel = hour => `${String(hour).padStart(2, '0')}:00`;

const buildEventsByHour = events => {
  const grouped = Object.fromEntries(dayHours.map(hour => [hour, []]));
  events.forEach(event => {
    if (!event?.scheduled_at) return;
    const parsedDate = parseScheduledDate(event.scheduled_at);
    if (!parsedDate) return;
    const hour = parsedDate.getHours();
    if (Number.isNaN(hour) || hour < 0 || hour > 23) return;
    grouped[hour].push(event);
  });
  return grouped;
};

const daysInMonth = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    days.push({
      date,
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    days.push({
      date,
      isCurrentMonth: true,
      events: getEventsForDate(date),
    });
  }

  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  return days;
});

const getWeekDays = () => {
  const today = selectedDate.value || new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - start.getDay());

  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push({
      date,
      isCurrentMonth: date.getMonth() === currentMonth.value.getMonth(),
      events: getEventsForDate(date),
      eventsByHour: buildEventsByHour(getEventsForDate(date)),
    });
  }

  return days;
};

const visibleDays = computed(() => {
  if (props.calendarViewMode === 'week') {
    return getWeekDays();
  }

  return daysInMonth.value;
});

const weeks = computed(() => {
  const days =
    props.calendarViewMode === 'week' ? visibleDays.value : daysInMonth.value;
  const groupedWeeks = [];

  for (let i = 0; i < days.length; i += 7) {
    groupedWeeks.push(days.slice(i, i + 7));
  }

  return groupedWeeks;
});

const currentMonthLabel = computed(() => {
  return currentMonth.value.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
});

const selectedDayLabel = computed(() => {
  return selectedDate.value.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
});

const selectedDayEvents = computed(() => {
  return getEventsForDate(selectedDate.value).sort((a, b) => {
    return (a.scheduled_at || 0) - (b.scheduled_at || 0);
  });
});

const getStatusText = status => {
  const statuses = {
    pending: 'PENDING',
    sending: 'SENDING',
    sent: 'SENT',
    failed: 'FAILED',
    skipped: 'SKIPPED',
  };
  return t(`SCHEDULE_MESSAGES.STATUS.${statuses[status] || 'PENDING'}`);
};

const getStatusClass = status => ({
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300':
    status === 'pending',
  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300':
    status === 'sending',
  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':
    status === 'sent',
  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300':
    status === 'failed',
  'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300':
    status === 'skipped',
});

const isRecurrentEvent = event => event?.is_recurrent === true;

const previousMonth = () => {
  if (props.calendarViewMode === 'week') {
    const newDate = new Date(selectedDate.value);
    newDate.setDate(newDate.getDate() - 7);
    selectedDate.value = newDate;
    currentMonth.value = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    return;
  }

  const newDate = new Date(currentMonth.value);
  newDate.setMonth(newDate.getMonth() - 1);
  currentMonth.value = newDate;
};

const nextMonth = () => {
  if (props.calendarViewMode === 'week') {
    const newDate = new Date(selectedDate.value);
    newDate.setDate(newDate.getDate() + 7);
    selectedDate.value = newDate;
    currentMonth.value = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    return;
  }

  const newDate = new Date(currentMonth.value);
  newDate.setMonth(newDate.getMonth() + 1);
  currentMonth.value = newDate;
};

const goToToday = () => {
  const today = new Date();
  selectedDate.value = today;
  currentMonth.value = new Date(today.getFullYear(), today.getMonth(), 1);
};

const openDayEventsModal = day => {
  selectedDate.value = day.date;
  showDayEventsModal.value = true;
};

const closeDayEventsModal = () => {
  showDayEventsModal.value = false;
};

watch(
  () => props.todayTrigger,
  () => {
    goToToday();
  }
);

watch(
  () => props.messages,
  () => {
    if (!getEventsForDate(selectedDate.value).length && props.messages.length) {
      const firstScheduledDate = parseScheduledDate(props.messages[0].scheduled_at);
      if (!firstScheduledDate) return;
      selectedDate.value = firstScheduledDate;
      currentMonth.value = new Date(
        selectedDate.value.getFullYear(),
        selectedDate.value.getMonth(),
        1
      );
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <div class="flex items-center justify-between px-4 pb-3">
      <div class="flex items-center gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="previousMonth">
          <span class="i-lucide-chevron-left size-4" />
        </Button>
        <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100 capitalize">
          {{ currentMonthLabel }}
        </h3>
        <Button variant="ghost" color="slate" size="sm" @click="nextMonth">
          <span class="i-lucide-chevron-right size-4" />
        </Button>
      </div>
    </div>

    <div class="px-4 pb-4 flex-1 min-h-0 overflow-hidden flex flex-col">
      <div
        class="mb-2"
        :class="
          props.calendarViewMode === 'week'
            ? 'grid min-w-0 grid-cols-[4rem_repeat(7,minmax(0,1fr))] gap-2'
            : 'grid grid-cols-7 gap-2'
        "
      >
        <div v-if="props.calendarViewMode === 'week'" />
        <span
          v-for="(day, index) in (props.calendarViewMode === 'week' ? visibleDays : weekDays)"
          :key="props.calendarViewMode === 'week' ? day.date.toISOString() : day"
          class="text-[11px] font-medium text-slate-500 dark:text-slate-400 text-center"
        >
          <template v-if="props.calendarViewMode === 'week'">
            <div class="flex flex-col items-center gap-1 w-full px-1">
              <span class="text-[10px] uppercase opacity-60">{{ weekDays[index] }}</span>
              <span
                class="text-sm font-bold"
                :class="isToday(day.date) ? 'text-woot-500' : 'text-slate-700 dark:text-slate-200'"
              >
                {{ day.date.getDate() }}
              </span>
            </div>
          </template>
          <template v-else>
            {{ day }}
          </template>
        </span>
      </div>

      <div
        v-if="props.calendarViewMode === 'week'"
        class="calendar-grid flex-1 min-h-0 overflow-y-auto overflow-x-auto"
      >
        <div class="grid min-w-[920px] grid-cols-[4rem_repeat(7,minmax(0,1fr))]">
          <div class="sticky left-0 z-10 bg-white dark:bg-slate-900">
            <div
              v-for="hour in dayHours"
              :key="`hour-label-${hour}`"
              class="h-20 border-b border-slate-100 dark:border-slate-800/50 pr-2 pt-1 text-right"
            >
              <span class="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                {{ formatHourLabel(hour) }}
              </span>
            </div>
          </div>
          <div
            v-for="day in visibleDays"
            :key="day.date.toISOString()"
            class="min-w-0 border-r border-slate-200 dark:border-slate-700"
          >
            <button
              v-for="hour in dayHours"
              :key="`${day.date.toISOString()}-${hour}`"
              type="button"
              class="h-20 w-full border-b border-slate-100 dark:border-slate-800/50 p-1 text-left transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
              :class="{
                'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900': day.isCurrentMonth,
                'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50': !day.isCurrentMonth,
              }"
              @click="openDayEventsModal(day)"
            >
              <div
                v-if="day.eventsByHour[hour]?.length"
                class="space-y-1"
              >
                <p
                  v-for="(event, eventIndex) in day.eventsByHour[hour].slice(0, 2)"
                  :key="`${event.id || 'event'}-${eventIndex}`"
                  class="text-[10px] leading-4 truncate rounded bg-woot-500/10 px-1 py-0.5 flex items-center gap-1"
                  :class="
                    isRecurrentEvent(event)
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-slate-600 dark:text-slate-300'
                  "
                >
                  <span class="truncate">
                    {{ event.title || t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
                  </span>
                  <span
                    v-if="isRecurrentEvent(event)"
                    class="i-lucide-repeat size-3 shrink-0"
                  />
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="h-full min-h-0 flex flex-col gap-2 overflow-hidden">
        <div
          v-for="week in weeks"
          :key="week[0].date.toISOString()"
          class="grid grid-cols-7 gap-2 flex-1 min-h-0"
        >
          <button
            v-for="day in week"
            :key="day.date.toISOString()"
            type="button"
            class="rounded-lg border p-1.5 text-left transition-colors h-full min-h-0"
            :class="{
              'h-24': props.calendarViewMode === 'week',
              'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900': day.isCurrentMonth,
              'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50': !day.isCurrentMonth,
              'ring-2 ring-woot-500': isSameDay(day.date, selectedDate),
              'hover:bg-slate-50 dark:hover:bg-slate-800': true,
            }"
            @click="openDayEventsModal(day)"
          >
            <div class="flex items-center justify-between">
              <span
                class="text-xs"
                :class="{
                  'text-slate-900 dark:text-slate-100': day.isCurrentMonth,
                  'text-slate-400 dark:text-slate-500': !day.isCurrentMonth,
                  'font-semibold': isToday(day.date),
                }"
              >
                {{ day.date.getDate() }}
              </span>
              <span
                v-if="day.events.length"
                class="rounded-full bg-woot-500/10 text-woot-600 dark:text-woot-300 px-1.5 py-0.5 text-[10px] font-medium"
              >
                {{ day.events.length }}
              </span>
            </div>
            <div v-if="day.events.length" class="mt-1 space-y-1">
              <div
                v-for="event in day.events.slice(0, props.calendarViewMode === 'week' ? 3 : 2)"
                :key="event.id"
                class="text-[10px] leading-4 flex items-center gap-1"
                :class="
                  isRecurrentEvent(event)
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-slate-600 dark:text-slate-300'
                "
              >
                <span class="truncate">
                  {{ event.title || t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
                </span>
                <span
                  v-if="isRecurrentEvent(event)"
                  class="i-lucide-repeat size-3 shrink-0"
                />
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showDayEventsModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        @click.self="closeDayEventsModal"
      >
        <div class="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] border border-n-weak overflow-hidden flex flex-col">
          <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
            <div>
              <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100 capitalize">
                {{ selectedDayLabel }}
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ selectedDayEvents.length }} {{ t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
              </p>
            </div>
            <Button variant="ghost" color="slate" size="sm" @click="closeDayEventsModal">
              <span class="i-lucide-x size-4" />
            </Button>
          </div>

          <div class="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
            <div
              v-if="!selectedDayEvents.length"
              class="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400"
            >
              {{ t('SCHEDULE_MESSAGES.EMPTY_SUBTEXT') }}
            </div>
            <div
              v-for="msg in selectedDayEvents"
              :key="msg.id"
              class="rounded-xl border bg-n-solid-2 p-3"
              :class="
                isRecurrentEvent(msg)
                  ? 'border-green-200 dark:border-green-800/60 bg-green-50/40 dark:bg-green-950/20'
                  : 'border-n-weak'
              "
            >
              <div class="flex items-center justify-between gap-2">
                <div class="inline-flex items-center gap-1 min-w-0">
                  <span
                    class="text-sm font-medium truncate leading-5"
                    :class="
                      isRecurrentEvent(msg)
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-slate-900 dark:text-slate-100'
                    "
                  >
                    {{ msg.title || t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
                  </span>
                  <span
                    v-if="isRecurrentEvent(msg)"
                    class="i-lucide-repeat size-4 shrink-0 self-center text-green-700 dark:text-green-300"
                  />
                </div>
                <span
                  class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="getStatusClass(msg.status)"
                >
                  {{ getStatusText(msg.status) }}
                </span>
              </div>
              <p
                class="text-xs mt-1 whitespace-pre-wrap"
                :class="
                  isRecurrentEvent(msg)
                    ? 'text-green-700/90 dark:text-green-300/90'
                    : 'text-slate-500 dark:text-slate-400'
                "
              >
                {{ msg.message }}
              </p>
              <div
                v-if="isRecurrentEvent(msg)"
                class="mt-2 inline-flex items-center rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 text-[11px] font-medium"
              >
                {{ t('SCHEDULE_MESSAGES.FORM.RECURRENT_LABEL') }}
              </div>
              <div class="mt-3 flex items-center gap-2">
                <Button
                  v-if="msg.conversation_display_id"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="emit('openConversation', msg)"
                >
                  <span class="i-lucide-message-circle size-4 mr-1" />
                  {{ t('SCHEDULE_MESSAGES.LIST.OPEN_CONVERSATION') }}
                </Button>
                <Button
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  @click="emit('deleteMessage', msg)"
                >
                  <span class="i-lucide-trash-2 size-4 mr-1" />
                  {{ t('SCHEDULE_MESSAGES.LIST.DELETE') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.calendar-grid > div {
  min-height: 1920px; /* 24 horas * 80px */
}

.calendar-grid {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.calendar-grid::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
